"use client";

import React from "react";
import { Plus, Minus, Layers3 } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const faqs = [{
  question: "What is EloRAG exactly?",
  answer: "EloRAG is a ready-to-deploy RAG platform for businesses and agencies. It turns approved company knowledge into grounded AI answers with citations, controlled retrieval, and refusal when evidence is insufficient."
}, {
  question: "How is EloRAG different from generic chat-with-documents tools?",
  answer: "Generic tools can work for simple experiments, but EloRAG is built for real business deployment. It adds approved-only retrieval, citation-based answers, refusal logic, tenant-aware delivery, BYO-Key cost control, and white-label readiness."
}, {
  question: "Can the AI answer from outside our approved content?",
  answer: "No. EloRAG is designed to answer from approved content only. Draft and Review content remain unavailable to the retrieval layer until your team explicitly approves it."
}, {
  question: "What happens if the AI cannot find enough evidence?",
  answer: "EloRAG refuses to answer when approved source coverage is insufficient. It does not guess, fill in missing information, or generate unsupported business answers."
}, {
  question: "Are citations included in answers?",
  answer: "Yes. EloRAG is designed to return citation-based answers so users can trace responses back to the supporting approved sources."
}, {
  question: "How does the BYO-Key and BYO-OCR model work?",
  answer: "Customers provide their own LLM and OCR API keys. EloRAG charges for the platform layer, while variable model and OCR usage are billed directly by the chosen providers."
}, {
  question: "Is EloRAG suitable for agencies and resellers?",
  answer: "Yes. EloRAG supports multi-tenant delivery and white-label deployments for agencies and resellers that want to launch branded RAG environments for clients."
}, {
  question: "Does EloRAG include unlimited usage?",
  answer: "No. All plans use hard backend-enforced caps. When limits are reached, customers can upgrade their plan or add Capacity Packs."
}];
export default function Faq() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: listRef,
    isVisible: isListVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="faq" className="bg-secondary/45 py-16 sm:py-20">
      <div className="mx-auto max-w-4xl px-6 lg:px-8">
        <div ref={headerRef} className={`mx-auto max-w-2xl text-center transition-all duration-700 ease-out ${isHeaderVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
            <Layers3 className="size-4" />
            <span>RAG Platform FAQs</span>
          </div>

          <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            Frequently asked questions about EloRAG
          </h2>
          <p className="mt-4 text-lg leading-8 text-muted-foreground">
            Key questions about EloRAG’s retrieval model, answer quality, deployment structure, and pricing approach.
          </p>
        </div>

        <div ref={listRef} className={`mt-12 transition-all duration-700 delay-200 ease-out ${isListVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          <Accordion type="single" collapsible className="w-full space-y-4">
            {faqs.map((faq, i) => <AccordionItem key={i} value={`item-${i}`} data-index={i} className="rounded-lg border border-border/70 bg-background px-6 shadow-[0_8px_20px_-16px_rgba(0,0,0,0.12)]">
                <AccordionTrigger className="group py-6 text-left text-lg font-semibold text-foreground hover:no-underline [&>svg:last-child]:hidden sm:text-xl">
                  <span className="pr-8">{faq.question}</span>
                  <div className="ml-auto shrink-0 text-primary transition-transform duration-200">
                    <Plus className="block size-6 group-data-[state=open]:hidden" />
                    <Minus className="hidden size-6 group-data-[state=open]:block" />
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-6 pr-12 text-base leading-relaxed text-muted-foreground">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </div>
      </div>
    </section>;
}